import { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import {
  Save, Loader2, Plus, Trash2, GripVertical, ToggleLeft, ToggleRight,
  MessageSquare, Users, Zap, Phone, RefreshCw, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle2,
} from 'lucide-react';

interface QualifyingQuestion {
  key: string;
  label: string;
  type: 'text' | 'select' | 'number';
  required: boolean;
  options?: string[];
}

interface AssignmentRule {
  name: string;
  type: 'product_match' | 'location_match' | 'fallback';
  field: string;
  values: string[];
  assignToUserIds: string[];
  strategy?: 'round_robin' | 'least_loaded';
}

interface LeadAutomationConfig {
  chatKartVerifyEnabled: boolean;
  qualifyingQuestions: QualifyingQuestion[];
  spamFilterEnabled: boolean;
  spamKeywords: string[];
  welcomeMessage: string;
  verifiedMessage: string;
  rejectedMessage: string;
  autoAssignEnabled: boolean;
  defaultPipelineId: string;
  defaultStageId: string;
  assignmentStrategy: string;
  assignmentRules: AssignmentRule[];
  autoCreateDeal: boolean;
  autoAddToCampaign: boolean;
  defaultCampaignId: string;
  dispositionStageMap: Record<string, string>;
}

interface VerificationStats {
  pending: number;
  inProgress: number;
  verified: number;
  rejected: number;
  expired: number;
  spam: number;
  total: number;
}

// ─── Section wrapper ─────────────────────────────────────────
function Section({ title, description, icon: Icon, expanded, onToggle, children }: {
  title: string; description: string; icon: any; expanded: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button onClick={onToggle} className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-gray-50">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-[var(--ink-0)]" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500">{description}</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {expanded && <div className="border-t border-gray-200 px-5 py-4">{children}</div>}
    </div>
  );
}

export function LeadAutomationTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [config, setConfig] = useState<LeadAutomationConfig | null>(null);
  const [stats, setStats] = useState<VerificationStats | null>(null);
  const [users, setUsers] = useState<{ id: string; firstName: string; lastName: string; email: string }[]>([]);
  const [pipelines, setPipelines] = useState<{ id: string; name: string; stages: { id: string; name: string }[] }[]>([]);
  const [campaigns, setCampaigns] = useState<{ id: string; name: string }[]>([]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    verification: true, questions: false, spam: false, assignment: false, deal: false, telecaller: false,
  });

  const toggleSection = (key: string) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, statsRes, usersRes, pipelinesRes] = await Promise.all([
        api.get('/lead-automation/config'),
        api.get('/lead-automation/stats').catch(() => ({ data: { data: null } })),
        api.get('/settings/users').catch(() => ({ data: { data: [] } })),
        api.get('/deals/pipelines').catch(() => ({ data: { data: [] } })),
      ]);

      setConfig(configRes.data.data);
      setStats(statsRes.data.data);
      setUsers(Array.isArray(usersRes.data.data) ? usersRes.data.data : []);
      setPipelines(Array.isArray(pipelinesRes.data.data) ? pipelinesRes.data.data : []);

      // Try loading telecaller campaigns
      try {
        const campRes = await api.get('/telecaller/campaigns?limit=100');
        setCampaigns(Array.isArray(campRes.data.data) ? campRes.data.data : []);
      } catch {}
    } catch (err: any) {
      showToast('error', 'Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await api.put('/lead-automation/config', config);
      showToast('success', 'Configuration saved');
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncChatKart = async () => {
    setSyncing(true);
    try {
      await api.post('/lead-automation/config/sync-chatkart');
      showToast('success', 'Config synced to ChatKart');
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to sync with ChatKart');
    } finally {
      setSyncing(false);
    }
  };

  const updateConfig = (updates: Partial<LeadAutomationConfig>) => {
    if (!config) return;
    setConfig({ ...config, ...updates });
  };

  // ─── Question management ──────────────────────────────────
  const addQuestion = () => {
    if (!config) return;
    const questions = [...config.qualifyingQuestions, {
      key: `q_${Date.now()}`,
      label: '',
      type: 'text' as const,
      required: true,
      options: [],
    }];
    updateConfig({ qualifyingQuestions: questions });
  };

  const removeQuestion = (idx: number) => {
    if (!config) return;
    updateConfig({ qualifyingQuestions: config.qualifyingQuestions.filter((_, i) => i !== idx) });
  };

  const updateQuestion = (idx: number, updates: Partial<QualifyingQuestion>) => {
    if (!config) return;
    const questions = [...config.qualifyingQuestions];
    questions[idx] = { ...questions[idx], ...updates };
    updateConfig({ qualifyingQuestions: questions });
  };

  // ─── Assignment rule management ────────────────────────────
  const addRule = () => {
    if (!config) return;
    const rules = [...config.assignmentRules, {
      name: '',
      type: 'fallback' as const,
      field: '',
      values: [],
      assignToUserIds: [],
    }];
    updateConfig({ assignmentRules: rules });
  };

  const removeRule = (idx: number) => {
    if (!config) return;
    updateConfig({ assignmentRules: config.assignmentRules.filter((_, i) => i !== idx) });
  };

  const updateRule = (idx: number, updates: Partial<AssignmentRule>) => {
    if (!config) return;
    const rules = [...config.assignmentRules];
    rules[idx] = { ...rules[idx], ...updates };
    updateConfig({ assignmentRules: rules });
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[var(--ink-0)]" /></div>;
  if (!config) return <div className="py-10 text-center text-sm text-gray-500">Failed to load configuration</div>;

  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
  const lbl = 'mb-1 block text-xs font-medium text-gray-700';
  const selectedPipeline = pipelines.find(p => p.id === config.defaultPipelineId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Lead Automation</h2>
          <p className="text-sm text-gray-500">WhatsApp verification, auto-assignment, deal creation & AURA Sales App integration.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSyncChatKart} disabled={syncing} className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} /> Sync to ChatKart
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      </div>

      {/* Stats Banner */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-7 gap-2 rounded-lg bg-gray-50 p-3">
          {[
            { label: 'Pending', value: stats.pending, color: 'text-yellow-600' },
            { label: 'In Progress', value: stats.inProgress, color: 'text-blue-600' },
            { label: 'Verified', value: stats.verified, color: 'text-green-600' },
            { label: 'Rejected', value: stats.rejected, color: 'text-red-600' },
            { label: 'Expired', value: stats.expired, color: 'text-gray-500' },
            { label: 'Spam', value: stats.spam, color: 'text-orange-600' },
            { label: 'Total', value: stats.total, color: 'text-gray-900 font-bold' },
          ].map(s => (
            <div key={s.label} className="text-center">
              <div className={`text-lg font-semibold ${s.color}`}>{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Section 1: WhatsApp Verification */}
      <Section title="WhatsApp Verification" description="Send qualifying questions via ChatKart WhatsApp" icon={MessageSquare} expanded={expandedSections.verification} onToggle={() => toggleSection('verification')}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-700">Enable ChatKart Verification</span>
              <p className="text-xs text-gray-500">Automatically send WhatsApp questions to new leads</p>
            </div>
            <button onClick={() => updateConfig({ chatKartVerifyEnabled: !config.chatKartVerifyEnabled })} className="text-gray-400 hover:text-[var(--ink-0)]">
              {config.chatKartVerifyEnabled ? <ToggleRight className="h-8 w-8 text-[var(--ink-0)]" /> : <ToggleLeft className="h-8 w-8" />}
            </button>
          </div>

          <div>
            <label className={lbl}>Welcome Message</label>
            <textarea className={inp} rows={2} value={config.welcomeMessage || ''} onChange={e => updateConfig({ welcomeMessage: e.target.value })} placeholder="Hi! Thanks for your interest. Let me help you with a few quick questions." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>Verified Message</label>
              <textarea className={inp} rows={2} value={config.verifiedMessage || ''} onChange={e => updateConfig({ verifiedMessage: e.target.value })} placeholder="Thank you! Your information has been received. Our team will contact you shortly." />
            </div>
            <div>
              <label className={lbl}>Rejected Message</label>
              <textarea className={inp} rows={2} value={config.rejectedMessage || ''} onChange={e => updateConfig({ rejectedMessage: e.target.value })} placeholder="Sorry, we are unable to proceed with your request at this time." />
            </div>
          </div>
        </div>
      </Section>

      {/* Section 2: Qualifying Questions */}
      <Section title="Qualifying Questions" description="Questions sent to leads via WhatsApp" icon={Phone} expanded={expandedSections.questions} onToggle={() => toggleSection('questions')}>
        <div className="space-y-3">
          {config.qualifyingQuestions.map((q, idx) => (
            <div key={idx} className="flex items-start gap-2 rounded-lg border border-gray-100 bg-gray-50 p-3">
              <GripVertical className="mt-2 h-4 w-4 flex-shrink-0 text-gray-300" />
              <div className="flex-1 space-y-2">
                <div className="grid grid-cols-4 gap-2">
                  <div className="col-span-1">
                    <label className={lbl}>Key</label>
                    <input className={inp} value={q.key} onChange={e => updateQuestion(idx, { key: e.target.value })} placeholder="field_key" />
                  </div>
                  <div className="col-span-2">
                    <label className={lbl}>Question</label>
                    <input className={inp} value={q.label} onChange={e => updateQuestion(idx, { label: e.target.value })} placeholder="What is your name?" />
                  </div>
                  <div>
                    <label className={lbl}>Type</label>
                    <select className={inp} value={q.type} onChange={e => updateQuestion(idx, { type: e.target.value as any })}>
                      <option value="text">Text</option>
                      <option value="select">Select</option>
                      <option value="number">Number</option>
                    </select>
                  </div>
                </div>
                {q.type === 'select' && (
                  <div>
                    <label className={lbl}>Options (comma-separated)</label>
                    <input className={inp} value={(q.options || []).join(', ')} onChange={e => updateQuestion(idx, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="Option 1, Option 2, Option 3" />
                  </div>
                )}
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={q.required} onChange={e => updateQuestion(idx, { required: e.target.checked })} className="rounded border-gray-300" />
                  Required
                </label>
              </div>
              <button onClick={() => removeQuestion(idx)} className="mt-2 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button onClick={addQuestion} className="flex items-center gap-2 text-sm text-[var(--ink-0)] hover:text-[var(--ink-0)]">
            <Plus className="h-4 w-4" /> Add Question
          </button>
        </div>
      </Section>

      {/* Section 3: Spam Filter */}
      <Section title="Spam Filter" description="Block spam responses during qualification" icon={AlertCircle} expanded={expandedSections.spam} onToggle={() => toggleSection('spam')}>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Enable Spam Filter</span>
            <button onClick={() => updateConfig({ spamFilterEnabled: !config.spamFilterEnabled })} className="text-gray-400 hover:text-[var(--ink-0)]">
              {config.spamFilterEnabled ? <ToggleRight className="h-8 w-8 text-[var(--ink-0)]" /> : <ToggleLeft className="h-8 w-8" />}
            </button>
          </div>
          {config.spamFilterEnabled && (
            <div>
              <label className={lbl}>Spam Keywords (comma-separated)</label>
              <textarea className={inp} rows={2} value={config.spamKeywords.join(', ')} onChange={e => updateConfig({ spamKeywords: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="spam, fake, test, xxx" />
            </div>
          )}
        </div>
      </Section>

      {/* Section 4: Auto-Assignment */}
      <Section title="Auto-Assignment" description="Automatically assign verified leads to team members" icon={Users} expanded={expandedSections.assignment} onToggle={() => toggleSection('assignment')}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Enable Auto-Assignment</span>
            <button onClick={() => updateConfig({ autoAssignEnabled: !config.autoAssignEnabled })} className="text-gray-400 hover:text-[var(--ink-0)]">
              {config.autoAssignEnabled ? <ToggleRight className="h-8 w-8 text-[var(--ink-0)]" /> : <ToggleLeft className="h-8 w-8" />}
            </button>
          </div>

          {config.autoAssignEnabled && (
            <>
              <div>
                <label className={lbl}>Default Strategy</label>
                <select className={inp} value={config.assignmentStrategy} onChange={e => updateConfig({ assignmentStrategy: e.target.value })}>
                  <option value="round_robin">Round Robin</option>
                  <option value="least_loaded">Least Loaded (fewest open deals)</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700">Assignment Rules (priority order — first match wins)</label>
                {config.assignmentRules.map((rule, idx) => (
                  <div key={idx} className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className={lbl}>Rule Name</label>
                        <input className={inp} value={rule.name} onChange={e => updateRule(idx, { name: e.target.value })} placeholder="North India Sales" />
                      </div>
                      <div>
                        <label className={lbl}>Match Type</label>
                        <select className={inp} value={rule.type} onChange={e => updateRule(idx, { type: e.target.value as any })}>
                          <option value="product_match">Product Match</option>
                          <option value="location_match">Location Match</option>
                          <option value="fallback">Fallback (catch-all)</option>
                        </select>
                      </div>
                      <div>
                        <label className={lbl}>Match Field</label>
                        <input className={inp} value={rule.field} onChange={e => updateRule(idx, { field: e.target.value })} placeholder="product / location" disabled={rule.type === 'fallback'} />
                      </div>
                    </div>
                    {rule.type !== 'fallback' && (
                      <div>
                        <label className={lbl}>Match Values (comma-separated)</label>
                        <input className={inp} value={rule.values.join(', ')} onChange={e => updateRule(idx, { values: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="Delhi, Noida, Gurgaon" />
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className={lbl}>Assign To</label>
                        <select multiple className={`${inp} h-20`} value={rule.assignToUserIds} onChange={e => updateRule(idx, { assignToUserIds: Array.from(e.target.selectedOptions, o => o.value) })}>
                          {users.map(u => (
                            <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.email})</option>
                          ))}
                        </select>
                      </div>
                      <button onClick={() => removeRule(idx)} className="mb-1 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
                <button onClick={addRule} className="flex items-center gap-2 text-sm text-[var(--ink-0)] hover:text-[var(--ink-0)]">
                  <Plus className="h-4 w-4" /> Add Rule
                </button>
              </div>
            </>
          )}
        </div>
      </Section>

      {/* Section 5: Deal Auto-Creation */}
      <Section title="Deal Auto-Creation" description="Automatically create deals for verified leads" icon={Zap} expanded={expandedSections.deal} onToggle={() => toggleSection('deal')}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Auto-Create Deal on Verification</span>
            <button onClick={() => updateConfig({ autoCreateDeal: !config.autoCreateDeal })} className="text-gray-400 hover:text-[var(--ink-0)]">
              {config.autoCreateDeal ? <ToggleRight className="h-8 w-8 text-[var(--ink-0)]" /> : <ToggleLeft className="h-8 w-8" />}
            </button>
          </div>

          {config.autoCreateDeal && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Default Pipeline</label>
                <select className={inp} value={config.defaultPipelineId || ''} onChange={e => updateConfig({ defaultPipelineId: e.target.value, defaultStageId: '' })}>
                  <option value="">Select Pipeline</option>
                  {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className={lbl}>Default Stage</label>
                <select className={inp} value={config.defaultStageId || ''} onChange={e => updateConfig({ defaultStageId: e.target.value })}>
                  <option value="">Select Stage</option>
                  {selectedPipeline?.stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* Section 6: AURA Sales App Integration */}
      <Section title="AURA Sales App Integration" description="Auto-push verified leads to AURA Sales App for telecalling" icon={Phone} expanded={expandedSections.telecaller} onToggle={() => toggleSection('telecaller')}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Auto-Push to AURA Sales App</span>
            <button onClick={() => updateConfig({ autoAddToCampaign: !config.autoAddToCampaign })} className="text-gray-400 hover:text-[var(--ink-0)]">
              {config.autoAddToCampaign ? <ToggleRight className="h-8 w-8 text-[var(--ink-0)]" /> : <ToggleLeft className="h-8 w-8" />}
            </button>
          </div>

          {config.autoAddToCampaign && (
            <div>
              <label className={lbl}>Default Campaign</label>
              <select className={inp} value={config.defaultCampaignId || ''} onChange={e => updateConfig({ defaultCampaignId: e.target.value })}>
                <option value="">Select Campaign</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
        </div>
      </Section>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {toast.message}
        </div>
      )}
    </div>
  );
}
